import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import { useDownloadTerminalServiceDocument } from "@features/terminal-services/hooks/useDownloadTerminalServiceDocument";
import type {
  PiggingServiceDetailsData,
  TerminalServiceUsageEntry,
} from "@services/terminal-services/types";
import { useTranslation } from "react-i18next";
import type { TerminalServiceDetailsViewProps } from "../types";
import { formatTerminalServiceDateTime } from "../utils";
import DocumentCard from "./DocumentCard";
import UsageView from "./UsageView";

const SERVICE = "bff-terminal-service";
const TEST_ID_BASE = "pigging-service-details";
const LANG_BASE = "terminal-services.details";
const EMPTY_VALUE = "-";

const detailLabelClassName = "w-[52%] shrink-0";
const detailValueClassName = "w-[48%]";
const detailLabelTextClassName = "font-normal text-[#646A77]";
const detailValueTextClassName = "font-normal text-[#31343B]";
const detailRowClassName = "border-b border-[#F1F2F4] pb-3";

const valueOrEmpty = (value?: string | number | null) => {
  if (value === null || value === undefined) {
    return EMPTY_VALUE;
  }

  const normalized = String(value).trim();

  return normalized || EMPTY_VALUE;
};

const getUsageEntries = (usageHours?: TerminalServiceUsageEntry[] | null) =>
  Array.isArray(usageHours) ? usageHours : [];

const getCompanyValue = ({
  code,
  name,
  nameEnglish,
  other,
}: {
  code?: string;
  name?: string;
  nameEnglish?: string;
  other?: string | null;
}) => {
  const companyName = valueOrEmpty(nameEnglish || name || code);
  const otherValue = valueOrEmpty(other);

  if (companyName === EMPTY_VALUE) {
    return otherValue;
  }

  if (otherValue === EMPTY_VALUE) {
    return companyName;
  }

  return `${companyName} / ${otherValue}`;
};

interface DetailItem {
  testId: string;
  label: string;
  value: string;
}

const DetailItemRow = ({ testId, label, value, isLast }: DetailItem & { isLast?: boolean }) => (
  <PaperItem
    testId={testId}
    translate={label}
    text={value}
    wrapperStyle={isLast ? "" : detailRowClassName}
    tranStyle={detailLabelTextClassName}
    textStyles={detailValueTextClassName}
    labelClassName={detailLabelClassName}
    valueClassName={detailValueClassName}
  />
);

const PiggingServiceDetails = ({
  serviceType,
  terminalServiceDetails,
}: TerminalServiceDetailsViewProps) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadTerminalServiceDocument();

  const details = terminalServiceDetails?.serviceDetails as PiggingServiceDetailsData | undefined;

  const attachmentDocuments = details?.attachments ?? [];
  const serviceTypeLabel = t(`${LANG_BASE}.service-type.${serviceType}`);

  const leftColumnItems: DetailItem[] = [
    {
      testId: `${TEST_ID_BASE}-service-type`,
      label: t(`${LANG_BASE}.fields.service-type`),
      value: serviceTypeLabel,
    },
    {
      testId: `${TEST_ID_BASE}-booking-from`,
      label: t(`${LANG_BASE}.fields.booking-from`),
      value: formatTerminalServiceDateTime(details?.startingFrom),
    },
    {
      testId: `${TEST_ID_BASE}-on-behalf-company`,
      label: t(`${LANG_BASE}.fields.on-behalf-company`),
      value: getCompanyValue({
        code: details?.onBehalfCompanyCode,
        name: details?.onBehalfCompanyName,
        nameEnglish: details?.onBehalfCompanyNameEnglish,
        other: details?.onBehalfCompanyOther,
      }),
    },
    {
      testId: `${TEST_ID_BASE}-customer-jl-slot-size`,
      label: t(`${LANG_BASE}.fields.customer-jl-slot-size`),
      value: valueOrEmpty(details?.customerJlNo),
    },
    {
      testId: `${TEST_ID_BASE}-fott-dock-line-size`,
      label: t(`${LANG_BASE}.fields.fott-dock-line-size`),
      value: valueOrEmpty(details?.fottDockLine),
    },
    {
      testId: `${TEST_ID_BASE}-usage-reason`,
      label: t(`${LANG_BASE}.fields.usage-reason`),
      value: valueOrEmpty(details?.usageReason),
    },
  ];

  const rightColumnItems: DetailItem[] = [
    {
      testId: `${TEST_ID_BASE}-chargeable-party`,
      label: t(`${LANG_BASE}.fields.chargeable-party`),
      value: getCompanyValue({
        code: details?.chargeablePartyCompanyCode,
        name: details?.chargeablePartyCompanyName,
        nameEnglish: details?.chargeablePartyCompanyNameEnglish,
        other: details?.chargeablePartyOther,
      }),
    },
    {
      testId: `${TEST_ID_BASE}-customer-tank-no`,
      label: t(`${LANG_BASE}.fields.customer-tank-no`),
      value: valueOrEmpty(details?.customerTankNo),
    },
    {
      testId: `${TEST_ID_BASE}-product-type`,
      label: t(`${LANG_BASE}.fields.product-type`),
      value: valueOrEmpty(details?.productType),
    },
    {
      testId: `${TEST_ID_BASE}-remarks`,
      label: t(`${LANG_BASE}.fields.remarks`),
      value: valueOrEmpty(details?.remarks),
    },
  ];

  return (
    <div data-testid={TEST_ID_BASE} className="flex flex-col gap-8">
      <Paper variant="outlined">
        <p className="mb-4 font-bold text-[#31343B]">{t(`${LANG_BASE}.service-details-title`)}</p>

        <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
          <div>
            {leftColumnItems.map((item, index) => (
              <DetailItemRow
                key={item.testId}
                {...item}
                isLast={index === leftColumnItems.length - 1}
              />
            ))}
          </div>

          <div className="md:pt-[96px]">
            {rightColumnItems.map((item, index) => (
              <DetailItemRow
                key={item.testId}
                {...item}
                isLast={index === rightColumnItems.length - 1}
              />
            ))}
          </div>
        </div>
      </Paper>

      <UsageView
        serviceId={details?.id ?? terminalServiceDetails?.id}
        serviceType={serviceType}
        status={details?.status ?? terminalServiceDetails?.status}
        usageHours={getUsageEntries(details?.usageHours)}
        testIdBase={`${TEST_ID_BASE}-usage`}
      />

      {attachmentDocuments && attachmentDocuments.length > 0 && (
        <section>
          <h2 className="mb-4 text-base font-normal text-[#646A77]">
            {t(`${LANG_BASE}.documents-title`)}
          </h2>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {attachmentDocuments.map((document, index) => (
              <DocumentCard
                key={`${document.documentUrl}-${index}`}
                document={document}
                testIdBase={`${TEST_ID_BASE}-attachment-${index}`}
                title={document.documentName}
                showDocumentMetadata
                onPreview={() => handlePreview(SERVICE, document.documentUrl)}
                onDownload={() =>
                  handleDownload(SERVICE, document.documentUrl, document.documentName)
                }
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default PiggingServiceDetails;
